import React, { useContext, useRef } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { IoIosCloudUpload, IoIosWarning, IoMdArrowRoundBack, IoMdFunnel } from 'react-icons/io';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Loading from 'components/Loading';
import {
  checkDicomForLabeledKeypoints,
  checkDicomForLabeledPhases,
  checkDicomForLabeledSegmentations,
  checkDicomForLabeledViewLabel,
} from '../../../utilities/labelChecks';
import { getDicomMediaType } from '../../../utilities/mediaType';
import { getFrameRoute } from '../../../utilities/frameRoute';

import Canvas from './Canvas';
import Dicoms from './Dicoms';
import FilterSelector, { EMPTY_FILTERS, IFilterValues } from './FilterSelector';
import { FilterIconButton } from './FilterSelector/FilterSelector.style';
import Identifier from './Identifier';
import Keypointer from './Kepointer';
import Loader from './Loader';
import Phaser from './Phaser';
import Qualifier from './Qualifier';
import Segmentor from './Segmentor';
import SubHeader from './SubHeader';
import {
  Button,
  CanvasContainer,
  ContentContainer,
  HeaderContainer,
  IconContainer,
  LabelsContainer,
  LabelTag,
  LeftSideHeaderContainer,
  PageContainer,
  RightSideBar,
  ReviewFrameHeader,
  SideBarContainer,
  SmallLoaderContainer,
  StatusTag,
  StudyIdContainer,
  ViewerContainer,
  Tab,
  TabContainer,
} from './Viewer.style';
import {
  useGetDicomsQuery,
  useGetDicomLabelingJobsQuery,
  useGetFramesQuery,
  useGetFullAnnotationSetsQuery,
  useGetStudyLabelingJobsQuery,
  useGetViewLabelsQuery,
  useGetKeypointCollectionLabelsQuery,
  useGetPhaseLabelsQuery,
  useGetQualityLabelsQuery,
  useGetSegmentationLabelsQuery,
  useGetReviewFramesQuery,
  useGetSegmentationPredictionsQuery,
  useGetSegmentationReferencesQuery,
  useGetStudyQuery,
  useGetUsersQuery,
  useGetPnoExistsQuery,
  useGetViewPredictionsSummaryQuery,
} from '../../../redux/slices/icardio';
import { ClassDataContext, UserContext } from '../../../services/contexts';
import {
  IAnnotationSetsParams,
  IDicom,
  IDicomsParams,
  IDicomLabelingJob,
  IDicomLabelingJobsParams,
  IFrame,
  IFrameParams,
  IStudyLabelingJobsParams,
  IViewLabelsParams,
  IQualityLabelsParams,
  IPhaseLabelsParams,
  IKeypointCollectionLabelsParams,
  ISegmentationLabelsParams,
  ISegmentationPredictionsParams,
  ISegmentationReferencesParams,
  IStudy,
  IUsersParams,
  IViewLabel,
  IQualityLabel,
  IKeypointCollectionLabel,
  IPhaseLabel,
  IReviewFrame,
  ISegmentationLabel,
  ISegmentationClass,
} from '../../../models/icardio';
import { IUser } from 'models/auth';
import { useReviewMode } from 'services/providers';
import Predictions from './Predictions';
import References from './References';

const getSegmentationDirtyKey = (segmentation?: Partial<ISegmentationLabel> | null) => {
  if (!segmentation) return null;
  if (segmentation.uuid) return `uuid:${segmentation.uuid}`;
  if (segmentation.frame && segmentation.segmentationClass && segmentation.userEmail) {
    return `frame:${segmentation.frame}:class:${segmentation.segmentationClass}:user:${segmentation.userEmail}`;
  }
  return null;
};

const Viewer = () => {
  const { user } = useContext(UserContext);
  const { studyIcid, dicomIcid, frameIcid, frameIndex } = useParams();
  const navigate = useNavigate();

  const { isReviewMode, setIsReviewMode } = useReviewMode();
  const [searchParams] = useSearchParams();

  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const [viewFilters, setViewFilters] = useState<IFilterValues>(EMPTY_FILTERS);
  const pipelineFilterParam = viewFilters.pipelines.join(',') || undefined;
  const viewClassFilterParam = viewFilters.viewClasses.join(',') || undefined;

  const { data: study, isFetching: studyResponseIsLoading } = useGetStudyQuery(
    studyIcid as string,
    { skip: !studyIcid }
  );

  const usersParams: IUsersParams = { paginate: false };
  const fullStudyAnnotationSetsParams: IAnnotationSetsParams = { studyIcid, paginate: false };
  const studyLabelingJobsParams: IStudyLabelingJobsParams = { studyIcid, paginate: false };
  const dicomsParams: IDicomsParams = useMemo(
    () => ({
      studyIcid,
      paginate: false,
      pipeline: pipelineFilterParam,
      viewClass: viewClassFilterParam,
    }),
    [studyIcid, pipelineFilterParam, viewClassFilterParam]
  );
  const dicomLabelingJobsParams: IDicomLabelingJobsParams = useMemo(
    () => ({
      studyIcid,
      paginate: false,
      pipeline: pipelineFilterParam,
      viewClass: viewClassFilterParam,
    }),
    [studyIcid, pipelineFilterParam, viewClassFilterParam]
  );
  const framesParams: IFrameParams = { dicomIcid, paginate: false };
  const viewLabelsParams: IViewLabelsParams = { studyIcid, paginate: false };
  const phaseLabelsParams: IPhaseLabelsParams = { studyIcid, paginate: false };
  const qualityLabelsParams: IQualityLabelsParams = { studyIcid, paginate: false };
  const keypointCollectionLabelsParams: IKeypointCollectionLabelsParams = {
    studyIcid,
    paginate: false,
  };
  const segmentationLabelsParams: ISegmentationLabelsParams = useMemo(() => {
    const base: ISegmentationLabelsParams = study?.isIceStudy
      ? { dicomIcid, paginate: false }
      : { studyIcid, paginate: false };
    const sourceICID = searchParams.get('sourceICID');
    return sourceICID ? { ...base, sourceIcid: sourceICID } : base;
  }, [study?.isIceStudy, dicomIcid, studyIcid, searchParams]);
  const segmentationPredictionsParams: ISegmentationPredictionsParams = {
    dicomIcid,
    paginate: false,
  };
  const segmentationReferencesParams: ISegmentationReferencesParams = {
    studyIcid,
    paginate: false,
  };

  const { data: users, isFetching: usersResponseIsLoading } = useGetUsersQuery(usersParams) as {
    data: IUser[];
    isFetching: boolean;
  };
  const { data: annotationSets, isFetching: annotationSetIsLoading } =
    useGetFullAnnotationSetsQuery(fullStudyAnnotationSetsParams, { skip: !studyIcid });
  const { data: studyLabelingJobs, isFetching: studyLabelingJobResponseIsLoading } =
    useGetStudyLabelingJobsQuery(studyLabelingJobsParams, { skip: !studyIcid });
  const { data: dicomsResponse, isFetching: dicomsResponseIsLoading } = useGetDicomsQuery(
    dicomsParams,
    {
      skip: !studyIcid,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: dicomLabelingJobsResponse, isFetching: dicomLabelingJobsResponseIsLoading } =
    useGetDicomLabelingJobsQuery(dicomLabelingJobsParams, {
      skip: !studyIcid,
      refetchOnMountOrArgChange: true,
    });
  const { data: framesResponse, isFetching: framesResponseIsLoading } = useGetFramesQuery(
    framesParams,
    { skip: !dicomIcid }
  );
  const { data: viewLabelsResponse, isFetching: viewLabelResponseIsLoading } =
    useGetViewLabelsQuery(viewLabelsParams, { skip: !studyIcid }) as {
      data: IViewLabel[];
      isFetching: boolean;
    };
  const { data: qualityLabelsResponse, isFetching: qualityLabelsResponseIsLoading } =
    useGetQualityLabelsQuery(qualityLabelsParams, { skip: !studyIcid }) as {
      data: IQualityLabel[];
      isFetching: boolean;
    };
  const { data: phaseLabelsResponse, isFetching: phaseLabelsResponseIsLoading } =
    useGetPhaseLabelsQuery(phaseLabelsParams, { skip: !studyIcid }) as {
      data: IPhaseLabel[];
      isFetching: boolean;
    };
  const {
    data: keypointCollectionLabelsResponse,
    isFetching: keypointCollectionLabelsResponseIsLoading,
  } = useGetKeypointCollectionLabelsQuery(keypointCollectionLabelsParams, { skip: !studyIcid }) as {
    data: IKeypointCollectionLabel[];
    isFetching: boolean;
  };
  const { data: segmentationLabelsResponse, isFetching: segmentationLabelsResponseIsLoading } =
    useGetSegmentationLabelsQuery(segmentationLabelsParams, { skip: !study }) as {
      data: ISegmentationLabel[];
      isFetching: boolean;
    };
  const { data: reviewFramesRequiringAction } = useGetReviewFramesQuery(
    { studyIcid, status: 'IN_REVIEW', paginate: false },
    {
      skip: !studyIcid || !isReviewMode,
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: segmentationPredictionsResponse,
    isFetching: segmentationPredictionsResponseIsLoading,
  } = useGetSegmentationPredictionsQuery(segmentationPredictionsParams, { skip: !dicomIcid });
  const {
    data: segmentationReferencesResponse,
    isFetching: segmentationReferencesResponseIsLoading,
  } = useGetSegmentationReferencesQuery(segmentationReferencesParams);
  const { data: pnoExistsResponse, isLoading: pnoExistsResponseIsLoading } = useGetPnoExistsQuery(
    studyIcid ? { studyIcid } : undefined,
    { skip: !studyIcid }
  );

  const { classData } = useContext(ClassDataContext);
  const { viewClasses, phaseClasses, keypointCollectionClasses, segmentationClasses } = classData;

  const [dicomSideBarIsOpen, setDicomSideBarIsOpen] = useState(true);

  const [activeTool, setActiveTool] = useState('identifier');
  const [activeFrameIndex, setActiveFrameIndex] = useState(0);
  const [activeUser, setActiveUser] = useState<IUser>(user);
  const [dicom, setDicom] = useState<any>([]);
  const [frames, setFrames] = useState<IFrame[]>([]);
  const [videoState, setVideoState] = useState<any>(false);
  const [intervalId, setIntervalId] = useState<any>(null);
  const wasVideoPlayingRef = useRef(false);
  const [activeType, setActiveType] = useState('Standard');

  const [dataIsLoading, setDataIsLoading] = useState(false);
  const [dataIsSaving, setDataIsSaving] = useState(false);
  const [errorOccured, setErrorOccured] = useState(false);

  const [viewLabelChangesAreSaved, setViewLabelChangesAreSaved] = useState(true);
  const [qualityLabelChangesAreSaved, setQualityLabelChangesAreSaved] = useState(true);
  const [phaseLabelChangesAreSaved, setPhaseLabelLabelChangesAreSaved] = useState(true);
  const [keypointCollectionChangesAreSaved, setKeypointCollectionLabelLabelsChangesAreSaved] =
    useState(true);
  const [segmentationChangesAreSaved, setSegmentationChangesAreSaved] = useState(true);
  const [dicomJobChangesAreSaved, setDicomJobChangesAreSaved] = useState(true);
  const [dirtySegmentationKeys, setDirtySegmentationKeys] = useState<string[]>([]);

  const [allViewLabels, setViewLabels] = useState<any>(null);
  const [allQualityLabels, setQualityLabels] = useState<any>(null);
  const [allPhaseLabels, setPhaseLabels] = useState<any>(null);
  const [allKeypointCollectionLabels, setKeypointCollectionLabelLabels] = useState<any>(null);
  const [allSegmentationLabels, setSegmentationLabels] = useState<any>(null);

  const [segmentationPredictions, setSegmentationPredictions] = useState<any>(null);
  const [selectedSegmentationPredictionSource, setSelectedSegmentationPredictionSource] = useState<
    string | null | undefined
  >(undefined);
  const [segmentationReferences, setSegmentationReferences] = useState<any>(null);

  const [activeOtherSegmentationClasses, setActiveOtherSegmentationClasses] = useState<any>([]);
  const [activeSegmentationPredictionClasses, setActiveSegmentationPredictionClasses] =
    useState<any>([]);
  const [activeSegmentationReferenceClasses, setActiveSegmentationReferenceClasses] = useState<any>(
    []
  );

  const [activeKeypointCollectionClass, setActiveKeypointCollectionClass] = useState<any>(null);
  const [activeSegmentationClass, setActiveSegmentationClass] = useState<ISegmentationClass>();
  const [segmentationObjectToBeAdopted, setSegmentationObjectToBeAdopted] = useState<any | null>(
    null
  );

  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('labels');

  const [showMeasurements, setShowMeasurements] = useState(true);
  const [useOpenContours, setUseOpenContours] = useState(false);
  const [showOtherSegmentations, setShowOtherSegmentations] = useState(false);
  const [showSegmentationPredictions, setShowSegmentationPredictions] = useState(false);
  const [showSegmentationReferences, setShowSegmentationReferences] = useState(false);
  const wasReviewModeRef = useRef(false);

  const annotationSet = annotationSets?.[0];
  const annotationSetIcid = annotationSet?.icid;
  const studyLabelingJob = studyLabelingJobs?.[0];

  const backToDatasetLink = `/${annotationSetIcid}`;

  // Use dicoms directly from the /dicoms/ endpoint with filter support
  const dicoms = useMemo(
    () => (dicomsResponse as IDicom[])?.slice().sort((a, b) => (a?.index || 0) - (b?.index || 0)),
    [dicomsResponse]
  );
  // Use dicomLabelingJobs for status tracking with the same filters
  const dicomLabelingJobs = dicomLabelingJobsResponse as IDicomLabelingJob[];
  const rejectedDicomUuids = useMemo(
    () =>
      isReviewMode
        ? [
            ...new Set(
              (reviewFramesRequiringAction ?? [])
                .map(reviewFrame => reviewFrame.dicom)
                .filter((dicomUuid): dicomUuid is string => dicomUuid != null)
            ),
          ]
        : [],
    [isReviewMode, reviewFramesRequiringAction]
  );
  const sidebarDicoms = useMemo(() => {
    if (!isReviewMode) return dicoms;

    const rejectedDicoms = new Set(rejectedDicomUuids);
    return dicoms?.filter(dicom => rejectedDicoms.has(dicom.uuid));
  }, [dicoms, isReviewMode, rejectedDicomUuids]);

  const numberOfFrames = frames?.length;
  const numberOfSeconds = 1000 / dicom?.framesPerSecond;

  const identifierIsActive = activeTool === 'identifier';
  const phaserIsActive = activeTool === 'phaser';
  const keypointerIsActive = activeTool === 'keypointer';
  const segmentorIsActive = activeTool === 'segmentor';
  const identifierIsDisabled = false;
  const phaserIsDisabled = false;
  const keypointerIsDisabled = false;
  const segmentorIsDisabled = false;

  const viewLabels = useMemo(
    () => allViewLabels?.filter(label => label.userEmail === activeUser.email),
    [allViewLabels, activeUser]
  );
  const qualityLabels = useMemo(
    () => allQualityLabels?.filter(label => label.userEmail === activeUser.email),
    [allQualityLabels, activeUser]
  );
  const phaseLabels = useMemo(
    () => allPhaseLabels?.filter(label => label.userEmail === activeUser.email),
    [allPhaseLabels, activeUser]
  );
  const keypointCollectionLabels = useMemo(
    () => allKeypointCollectionLabels?.filter(label => label.userEmail === activeUser.email),
    [allKeypointCollectionLabels, activeUser]
  );
  const segmentationLabels = useMemo(() => {
    if (!Array.isArray(allSegmentationLabels)) return [];

    // Default filtering by active user
    const userLabels = allSegmentationLabels.filter(label => label.userEmail === activeUser?.email);
    // In review mode, if the reviewer hasn't created their own label yet,
    // include the original label being reviewed so they can see and edit it.
    if (isReviewMode) {
      const sourceICID = searchParams.get('sourceICID');
      if (sourceICID) {
        const sourceLabel = allSegmentationLabels.find(l => l.icid === sourceICID);
        if (sourceLabel) {
          const hasUserLabel = userLabels.some(
            l =>
              l.frame === sourceLabel.frame && l.segmentationClass === sourceLabel.segmentationClass
          );
          if (!hasUserLabel) {
            userLabels.push(sourceLabel);
          }
        }
      }
    }
    return userLabels;
  }, [allSegmentationLabels, activeUser, isReviewMode, searchParams]);
  const dirtySegmentations = useMemo(() => {
    if (!Array.isArray(allSegmentationLabels) || !dirtySegmentationKeys.length) return [];
    const dirtyKeySet = new Set(dirtySegmentationKeys);
    return allSegmentationLabels.filter(label => {
      const dirtyKey = getSegmentationDirtyKey(label);
      return dirtyKey ? dirtyKeySet.has(dirtyKey) : false;
    });
  }, [allSegmentationLabels, dirtySegmentationKeys]);

  const markSegmentationAsDirty = useCallback((segmentation: Partial<ISegmentationLabel>) => {
    const dirtyKey = getSegmentationDirtyKey(segmentation);
    if (!dirtyKey) return;
    setDirtySegmentationKeys(prevKeys =>
      prevKeys.includes(dirtyKey) ? prevKeys : [...prevKeys, dirtyKey]
    );
    setSegmentationChangesAreSaved(false);
  }, []);

  const replaceDirtySegmentationKey = useCallback(
    (
      previousSegmentation: Partial<ISegmentationLabel> | null,
      nextSegmentation: Partial<ISegmentationLabel>
    ) => {
      const previousKey = getSegmentationDirtyKey(previousSegmentation);
      const nextKey = getSegmentationDirtyKey(nextSegmentation);

      setDirtySegmentationKeys(prevKeys => {
        const filteredKeys = previousKey
          ? prevKeys.filter(dirtyKey => dirtyKey !== previousKey)
          : prevKeys;
        if (!nextKey || filteredKeys.includes(nextKey)) return filteredKeys;
        return [...filteredKeys, nextKey];
      });
      setSegmentationChangesAreSaved(false);
    },
    []
  );

  const clearDirtySegmentations = useCallback(() => {
    setDirtySegmentationKeys([]);
  }, []);

  const checkIfDicomIsLabeled = useCallback(
    dicom => {
      if (!dicom)
        return {
          viewLabelIsLabeled: false,
          viewLabelIsRequired: false,
          phaseLabelsAreLabeled: false,
          phaseLabelsAreRequired: false,
          keypointCollectionLabelsAreLabeled: false,
          keypointCollectionLabelsAreRequired: false,
          segmentationsAreLabeled: false,
          segmentationsAreRequired: false,
          dicomIsLabeled: false,
        };

      const { viewLabelIsLabeled, viewLabelIsRequired } = checkDicomForLabeledViewLabel({
        dicom,
        viewLabels,
        qualityLabels,
      });
      const { phaseLabelsAreLabeled, phaseLabelsAreRequired } = checkDicomForLabeledPhases({
        dicom,
        viewLabels,
        phaseClasses,
        phaseLabels,
      });
      const { keypointCollectionLabelsAreLabeled, keypointCollectionLabelsAreRequired } =
        checkDicomForLabeledKeypoints({
          dicom,
          viewLabels,
          phaseClasses,
          phaseLabels,
          keypointCollectionClasses,
          keypointCollectionLabels,
        });
      const { segmentationsAreLabeled, segmentationsAreRequired } =
        checkDicomForLabeledSegmentations({
          dicom,
          viewLabels,
          phaseLabels,
          segmentationClasses,
          segmentationLabels,
        });

      const dicomIsLabeled =
        viewLabelIsLabeled &&
        (phaseLabelsAreLabeled || !phaseLabelsAreRequired) &&
        (keypointCollectionLabelsAreLabeled || !keypointCollectionLabelsAreRequired) &&
        (segmentationsAreLabeled || !segmentationsAreRequired);

      return {
        viewLabelIsLabeled,
        viewLabelIsRequired,
        phaseLabelsAreLabeled,
        phaseLabelsAreRequired,
        keypointCollectionLabelsAreLabeled,
        keypointCollectionLabelsAreRequired,
        segmentationsAreLabeled,
        segmentationsAreRequired,
        dicomIsLabeled,
      };
    },
    [
      viewLabels,
      qualityLabels,
      phaseClasses,
      phaseLabels,
      keypointCollectionClasses,
      keypointCollectionLabels,
      segmentationClasses,
      segmentationLabels,
    ]
  );

  const activeFrame: IFrame = useMemo(() => frames?.[activeFrameIndex], [frames, activeFrameIndex]);
  const getFrameRouteWithReviewMode = useCallback(
    (frame: IFrame) => {
      if (!studyIcid || !dicomIcid) return null;

      const route = getFrameRoute(studyIcid, dicomIcid, frame);
      if (!isReviewMode) return route;

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('isReviewMode', 'true');
      return `${route}?${nextSearchParams}`;
    },
    [dicomIcid, isReviewMode, searchParams, studyIcid]
  );
  const setActiveFrameWithUrl = useCallback(
    (nextFrameIndex: React.SetStateAction<number>) => {
      const nextIndex =
        typeof nextFrameIndex === 'function' ? nextFrameIndex(activeFrameIndex) : nextFrameIndex;
      const nextFrame = frames?.[nextIndex];

      setActiveFrameIndex(nextIndex);
      if (!videoState && nextFrame) {
        const route = getFrameRouteWithReviewMode(nextFrame);
        if (route) navigate(route, { replace: true });
      }
    },
    [activeFrameIndex, frames, getFrameRouteWithReviewMode, navigate, videoState]
  );
  const activeDicom = useMemo(
    () => dicoms?.find(dicom => dicom?.icid === dicomIcid),
    [dicomIcid, dicoms]
  );
  const activeReviewFrame = useMemo(
    () =>
      reviewFramesRequiringAction?.find(
        reviewFrame =>
          reviewFrame.dicom === activeDicom?.uuid && reviewFrame.frameIndex === activeFrame?.index
      ),
    [activeDicom?.uuid, activeFrame?.index, reviewFramesRequiringAction]
  );
  const displayedSegmentationLabels = useMemo(() => {
    if (!isReviewMode || !activeReviewFrame?.userEmail) return segmentationLabels;

    const reviewedUserLabels = allSegmentationLabels?.filter(
      segmentation => segmentation.userEmail === activeReviewFrame.userEmail
    );
    const draftClassesByFrame = new Set(
      dirtySegmentations.map(label => `${label.frame}:${label.segmentationClass}`)
    );

    return [
      ...(reviewedUserLabels?.filter(
        label => !draftClassesByFrame.has(`${label.frame}:${label.segmentationClass}`)
      ) ?? []),
      ...dirtySegmentations,
    ];
  }, [
    activeReviewFrame?.userEmail,
    allSegmentationLabels,
    dirtySegmentations,
    isReviewMode,
    segmentationLabels,
  ]);
  const reviewFrameNavigation = useMemo(() => {
    const dicomOrder = new Map(
      (dicoms ?? []).map((reviewDicom, position) => [
        reviewDicom.uuid,
        reviewDicom.index ?? position,
      ])
    );
    const orderedReviewFrames = (reviewFramesRequiringAction ?? [])
      .filter(
        reviewFrame =>
          reviewFrame.dicom &&
          reviewFrame.dicomIcid &&
          reviewFrame.studyIcid &&
          Number.isSafeInteger(reviewFrame.frameIndex)
      )
      .sort((a, b) => {
        const dicomIndexDifference =
          (dicomOrder.get(a.dicom) ?? Number.MAX_SAFE_INTEGER) -
          (dicomOrder.get(b.dicom) ?? Number.MAX_SAFE_INTEGER);
        if (dicomIndexDifference !== 0) return dicomIndexDifference;

        const dicomIcidDifference = a.dicomIcid.localeCompare(b.dicomIcid);
        return dicomIcidDifference || a.frameIndex - b.frameIndex;
      });

    if (!activeDicom || activeFrame?.index == null) {
      return { previous: undefined, next: undefined };
    }

    const currentReviewIndex = orderedReviewFrames.findIndex(
      reviewFrame =>
        reviewFrame.dicom === activeDicom.uuid && reviewFrame.frameIndex === activeFrame.index
    );
    if (currentReviewIndex !== -1) {
      return {
        previous: orderedReviewFrames[currentReviewIndex - 1],
        next: orderedReviewFrames[currentReviewIndex + 1],
      };
    }

    const activeDicomOrder = dicomOrder.get(activeDicom.uuid) ?? Number.MAX_SAFE_INTEGER;
    const nextReviewIndex = orderedReviewFrames.findIndex(reviewFrame => {
      const reviewDicomOrder = dicomOrder.get(reviewFrame.dicom) ?? Number.MAX_SAFE_INTEGER;
      return (
        reviewDicomOrder > activeDicomOrder ||
        (reviewDicomOrder === activeDicomOrder && reviewFrame.frameIndex > activeFrame.index)
      );
    });

    return {
      previous:
        nextReviewIndex === -1
          ? orderedReviewFrames[orderedReviewFrames.length - 1]
          : orderedReviewFrames[nextReviewIndex - 1],
      next: nextReviewIndex === -1 ? undefined : orderedReviewFrames[nextReviewIndex],
    };
  }, [activeDicom, activeFrame?.index, dicoms, reviewFramesRequiringAction]);
  const navigateToReviewFrame = useCallback(
    (reviewFrame?: IReviewFrame) => {
      if (!reviewFrame) return;

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('isReviewMode', 'true');
      navigate(
        `/${reviewFrame.studyIcid}/${reviewFrame.dicomIcid}/frame/${reviewFrame.frameIndex}?${nextSearchParams}`
      );
    },
    [navigate, searchParams]
  );
  const goToPreviousReview = useCallback(
    () => navigateToReviewFrame(reviewFrameNavigation.previous),
    [navigateToReviewFrame, reviewFrameNavigation.previous]
  );
  const goToNextReview = useCallback(
    () => navigateToReviewFrame(reviewFrameNavigation.next),
    [navigateToReviewFrame, reviewFrameNavigation.next]
  );
  const rejectedFrameIndexes = useMemo(
    () =>
      isReviewMode
        ? [
            ...new Set(
              (reviewFramesRequiringAction ?? [])
                .filter(reviewFrame => reviewFrame.dicom === activeDicom?.uuid)
                .map(reviewFrame => reviewFrame.frameIndex)
            ),
          ]
        : [],
    [activeDicom?.uuid, isReviewMode, reviewFramesRequiringAction]
  );
  const activeDicomLabelingJob = useMemo(
    () => dicomLabelingJobs?.find(job => job?.dicom?.uuid === activeDicom?.uuid),
    [activeDicom, dicomLabelingJobs]
  );
  const activeViewLabel = useMemo(
    () => viewLabels?.find(viewLabel => viewLabel?.dicom === activeDicom?.uuid),
    [activeDicom, viewLabels]
  );
  const activeQualityLabel = useMemo(
    () => qualityLabels?.find(qualityLabel => qualityLabel?.dicom === activeDicom?.uuid),
    [activeDicom, qualityLabels]
  );
  const activePhaseClasses = useMemo(
    () =>
      phaseClasses?.filter(cardiacPhase =>
        cardiacPhase.viewClasses?.includes(activeViewLabel?.viewClass)
      ),
    [activeViewLabel?.viewClass, phaseClasses]
  );
  const activePhaseLabels = useMemo(
    () => phaseLabels?.filter(phaseLabel => phaseLabel?.dicom === activeDicom?.uuid),
    [activeDicom, phaseLabels]
  );
  const activeKeypointCollectionLabels = useMemo(
    () =>
      keypointCollectionLabels?.find(
        keypointCollectionLabel =>
          keypointCollectionLabel?.frame === activeFrame?.uuid &&
          keypointCollectionLabel?.keypointCollectionClass === activeKeypointCollectionClass?.uuid
      ),
    [activeFrame?.uuid, activeKeypointCollectionClass?.uuid, keypointCollectionLabels]
  );
  const activeSegmentation = useMemo(
    () =>
      displayedSegmentationLabels?.find(
        segmentation =>
          segmentation?.frame === activeFrame?.uuid &&
          segmentation?.segmentationClass === activeSegmentationClass?.uuid
      ),
    [displayedSegmentationLabels, activeFrame?.uuid, activeSegmentationClass?.uuid]
  );

  const allSegmentations = useMemo(
    () =>
      displayedSegmentationLabels?.filter(
        segmentation => segmentation?.frame === activeFrame?.uuid
      ),
    [displayedSegmentationLabels, activeFrame?.uuid]
  );

  useEffect(() => {
    if (!showOtherSegmentations) return;

    const labeledClasses = (allSegmentations ?? [])
      .filter(segmentation => !!segmentation?.paths?.length)
      .map(segmentation => segmentation.segmentationClassValue)
      .filter((value): value is string => !!value);

    setActiveOtherSegmentationClasses([...new Set(labeledClasses)]);
  }, [allSegmentations, showOtherSegmentations]);

  const segmentationPredictionSourceNames = useMemo(
    () =>
      Array.from(
        new Set((segmentationPredictions ?? []).map(prediction => prediction.sourceName))
      ) as (string | null)[],
    [segmentationPredictions]
  );

  const activeSegmentationPredictionSource =
    selectedSegmentationPredictionSource !== undefined &&
    segmentationPredictionSourceNames.includes(selectedSegmentationPredictionSource)
      ? selectedSegmentationPredictionSource
      : (segmentationPredictionSourceNames[0] ?? null);

  const allSegmentationPredictions = useMemo(
    () =>
      segmentationPredictions?.filter(
        segmentation =>
          segmentation?.frame === activeFrame?.uuid &&
          segmentation?.sourceName === activeSegmentationPredictionSource
      ),
    [segmentationPredictions, activeFrame?.uuid, activeSegmentationPredictionSource]
  );

  const allSegmentationReferences = useMemo(
    () => segmentationReferences?.filter(segmentation => segmentation?.frame === activeFrame?.uuid),
    [segmentationReferences, activeFrame?.uuid]
  );
  const dicomSegmentationReferences = useMemo(
    () => segmentationReferences?.filter(segmentation => segmentation?.dicom === activeDicom?.uuid),
    [segmentationReferences, activeDicom?.uuid]
  );

  const viewClass = useMemo(
    () => viewClasses?.find(viewClass => viewClass?.uuid === activeViewLabel?.viewClass),
    [activeViewLabel?.viewClass, viewClasses]
  );

  const activeDicomMediaType = getDicomMediaType(
    activeDicom?.mediaType,
    activeDicom?.numberOfFrames
  );

  const dicomViewClasses = useMemo(
    () =>
      viewClasses?.filter(
        viewClass => viewClass?.type === activeType && viewClass?.mediaType === activeDicomMediaType
      ),
    [viewClasses, activeDicomMediaType, activeType]
  );

  const dicomKeypointCollectionClasses = useMemo(
    () =>
      keypointCollectionClasses?.filter(keypointCollectionClass =>
        keypointCollectionClass?.viewClasses?.includes(viewClass?.uuid)
      ),
    [keypointCollectionClasses, viewClass?.uuid]
  );
  const dicomFeatures = useMemo(
    () =>
      study?.isIceStudy
        ? segmentationClasses
        : segmentationClasses?.filter(feature => feature?.viewClasses?.includes(viewClass?.uuid)),
    [study?.isIceStudy, segmentationClasses, viewClass?.uuid]
  );

  const {
    viewLabelIsLabeled,
    phaseLabelsAreLabeled,
    keypointCollectionLabelsAreLabeled,
    segmentationsAreLabeled,
    dicomIsLabeled,
  } = checkIfDicomIsLabeled(activeDicom);

  const showPhaser = useMemo(
    () => !study?.isIceStudy && !!activePhaseClasses?.length,
    [study?.isIceStudy, activePhaseClasses?.length]
  );
  const showKeypointer = useMemo(() => {
    const { keypointCollectionLabelsAreRequired } = checkDicomForLabeledKeypoints({
      dicom: activeDicom,
      viewLabels,
      phaseClasses,
      phaseLabels,
      keypointCollectionClasses,
      keypointCollectionLabels,
    });
    const result =
      // (phaseLabelsAreRequired ? phaseLabelsAreLabeled : true) &&
      // also show the keypointer when the view only has optional keypoint collections
      keypointCollectionLabelsAreRequired || !!dicomKeypointCollectionClasses?.length;
    return result;
  }, [
    activeDicom,
    dicomKeypointCollectionClasses?.length,
    keypointCollectionClasses,
    keypointCollectionLabels,
    phaseClasses,
    phaseLabels,
    viewLabels,
  ]);
  const showSegmentor = useMemo(() => {
    const { segmentationsAreRequired } = checkDicomForLabeledSegmentations({
      dicom: activeDicom,
      viewLabels,
      phaseLabels,
      segmentationClasses,
      segmentationLabels,
    });
    const hasSegmentationLabels = [allSegmentationLabels, segmentationLabelsResponse].some(labels =>
      labels?.some(segmentation => segmentation?.dicom === activeDicom?.uuid)
    );
    return segmentationsAreRequired || hasSegmentationLabels;
  }, [
    activeDicom,
    allSegmentationLabels,
    segmentationLabelsResponse,
    viewLabels,
    phaseClasses,
    phaseLabels,
    segmentationClasses,
    segmentationLabels,
  ]);

  useEffect(() => {
    if (segmentationLabelsResponseIsLoading) return;
    if (activeTool === 'segmentor' && !showSegmentor) setActiveTool('identifier');
  }, [activeTool, segmentationLabelsResponseIsLoading, showSegmentor]);

  useEffect(() => {
    const segmentorIsOnlyVisibleTool = showSegmentor && !showPhaser && !showKeypointer;
    if (isReviewMode && segmentorIsOnlyVisibleTool && activeTool !== 'segmentor') {
      setActiveTool('segmentor');
    }
  }, [activeTool, isReviewMode, showKeypointer, showPhaser, showSegmentor]);

  const previousButtonIsDisabled = activeTool === 'identifier';
  const nextButtonIsDisabled =
    activeTool === 'identifier'
      ? showPhaser || showKeypointer || showSegmentor
        ? false
        : true
      : activeTool === 'phaser'
        ? showKeypointer || showSegmentor
          ? false
          : true
        : activeTool === 'keypointer'
          ? showSegmentor
            ? false
            : true
          : true;

  const showWindowAndViewTag = useMemo(() => viewLabelIsLabeled, [viewLabelIsLabeled]);
  const showPhaseTag = useMemo(
    () => showPhaser && showWindowAndViewTag && phaseLabelsAreLabeled,
    [phaseLabelsAreLabeled, showPhaser, showWindowAndViewTag]
  );
  const showKeypointTag = useMemo(
    () => showKeypointer && showPhaseTag && keypointCollectionLabelsAreLabeled,
    [keypointCollectionLabelsAreLabeled, showKeypointer, showPhaseTag]
  );
  const showSegmentationTag = useMemo(
    () => showSegmentor && showKeypointTag && segmentationsAreLabeled,
    [showSegmentor, showKeypointTag, segmentationsAreLabeled]
  );

  const windowAndViewTag = useMemo(
    () => `${viewClass?.window} ${viewClass?.view}`,
    [viewClass?.view, viewClass?.window]
  );
  const phaseTag = useMemo(
    () => `${activePhaseLabels?.length} Phases`,
    [activePhaseLabels?.length]
  );
  const keypointTag = useMemo(() => {
    const phaseFrameUuids = activePhaseLabels?.map(phaseClass => phaseClass?.frame);
    const keypointCollectionClassUuids = dicomKeypointCollectionClasses?.map(
      keypointCollectionClass => keypointCollectionClass?.uuid
    );
    const labeledKeypointCollections = keypointCollectionLabels?.filter(
      keypointCollectionLabel =>
        phaseFrameUuids?.includes(keypointCollectionLabel?.frame) &&
        keypointCollectionClassUuids?.includes(keypointCollectionLabel?.keypointCollectionClass) &&
        !!keypointCollectionLabel?.keypointLabels?.length &&
        keypointCollectionLabel?.keypointLabels?.every(keypoint => keypoint?.x && keypoint?.y)
    );
    return `${labeledKeypointCollections?.length} Keypoint Collections`;
  }, [activePhaseLabels, dicomKeypointCollectionClasses, keypointCollectionLabels]);
  const segmentationTag = useMemo(() => {
    const usablePhases = activePhaseLabels?.filter(
      phaseLabel => phaseLabel?.phaseClass !== 'Mid-Systole'
    );
    const phaseFrameUuids = usablePhases?.map(phaseClass => phaseClass?.frame);
    const dicomFeatureValues = dicomFeatures?.map(feature => feature?.value);
    const labeledSegmentations = segmentationLabels?.filter(
      segmentation =>
        phaseFrameUuids?.includes(segmentation?.frame) &&
        dicomFeatureValues?.includes(segmentation?.segmentationClass?.value) &&
        !!segmentation?.paths?.length
    );
    return `${labeledSegmentations?.length} Segmentations`;
  }, [activePhaseLabels, dicomFeatures, segmentationLabels]);

  const changesAreSaved = useMemo(
    () =>
      viewLabelChangesAreSaved &&
      qualityLabelChangesAreSaved &&
      phaseLabelChangesAreSaved &&
      keypointCollectionChangesAreSaved &&
      segmentationChangesAreSaved &&
      dicomJobChangesAreSaved,
    [
      phaseLabelChangesAreSaved,
      keypointCollectionChangesAreSaved,
      viewLabelChangesAreSaved,
      qualityLabelChangesAreSaved,
      segmentationChangesAreSaved,
      dicomJobChangesAreSaved,
    ]
  );

  const handleChangeSegmentationClass = useCallback(
    (nextClass: any) => {
      setActiveSegmentationClass(prevClass => {
        // only act when editing and both classes are known
        if (
          editMode &&
          prevClass?.uuid &&
          nextClass?.uuid &&
          activeFrame?.uuid &&
          Array.isArray(segmentationLabels)
        ) {
          setSegmentationLabels(prevLabels => {
            if (!Array.isArray(prevLabels)) return prevLabels;

            // the label currently being edited: (frame, prevClass)
            const movingIdx = prevLabels.findIndex(
              s => s.frame === activeFrame.uuid && s.segmentationClass === prevClass.uuid
            );
            if (movingIdx === -1) return prevLabels; // nothing to move

            const moving = prevLabels[movingIdx];

            // drop any label that would conflict on the target (same frame, nextClass) and also drop the old one
            const filtered = prevLabels.filter(
              s =>
                !(
                  s.frame === activeFrame.uuid &&
                  (s.segmentationClass === prevClass.uuid || s.segmentationClass === nextClass.uuid)
                )
            );

            const updated = {
              ...moving,
              segmentationClass: nextClass.uuid,
              segmentationClassValue: nextClass.value,
              updatedAt: new Date().toISOString(),
            };

            replaceDirtySegmentationKey(moving, updated);
            return [...filtered, updated];
          });
        }
        return nextClass; // finally switch the active class
      });
    },
    [
      editMode,
      activeFrame?.uuid,
      segmentationLabels,
      replaceDirtySegmentationKey,
      setSegmentationLabels,
      setActiveSegmentationClass,
    ]
  );

  const handleAdoptSegmentation = () => {
    if (!segmentationObjectToBeAdopted || editMode) return;

    const segmentationObjectSourceUuid = segmentationObjectToBeAdopted.uuid;

    setActiveTab('labels');
    setShowSegmentationPredictions(false);
    setShowSegmentationReferences(false);

    // check whether the adopted segmentation is prediction or reference
    const isPrediction = allSegmentationPredictions.some(
      prediction => prediction.uuid === segmentationObjectSourceUuid
    );

    const isReference = allSegmentationReferences.some(
      reference => reference.uuid === segmentationObjectSourceUuid
    );

    const isLabel = segmentationLabelsResponse.some(
      label => label.uuid === segmentationObjectSourceUuid
    );

    const sourceType = isPrediction
      ? 'SEGMENTATION_PREDICTION'
      : isReference
        ? 'SEGMENTATION_REFERENCE'
        : isLabel
          ? 'SEGMENTATION_LABEL'
          : '';

    const activeSegmentationUuid = isReviewMode
      ? segmentationLabelsResponse?.find(
          label =>
            label.frame === segmentationObjectToBeAdopted.frame &&
            label.segmentationClass === segmentationObjectToBeAdopted.segmentationClass &&
            label.userEmail === user?.profile?.email
        )?.uuid
      : activeSegmentation?.uuid;

    const userEmail = user?.profile?.email;

    const newSegmentationLabel = {
      dicom: segmentationObjectToBeAdopted.dicom,
      frame: segmentationObjectToBeAdopted.frame,
      frameIndex: segmentationObjectToBeAdopted.frameIndex,
      isFullyVisible: true,
      paths: segmentationObjectToBeAdopted.paths,
      segmentationClass: segmentationObjectToBeAdopted.segmentationClass,
      segmentationClassValue: segmentationObjectToBeAdopted.segmentationClassValue,
      sourceUuid: segmentationObjectSourceUuid,
      sourceType,
      uuid: activeSegmentationUuid,
      userEmail: userEmail,
    };

    if (isReviewMode) {
      if (activeViewLabel) {
        setViewLabel({
          viewClassValue: activeViewLabel.viewClassValue,
          dicom: activeViewLabel.dicom,
          viewClass: activeViewLabel.viewClass,
          userEmail,
        });
      }
      setActiveUser({
        email: userEmail,
      });
      setIsReviewMode(false);
    }

    setSegmentation(newSegmentationLabel);
  };

  const handleDicomTypeOnClick = () => {
    const typeToggleMap = {
      Color: 'Standard',
      Standard: 'Color',
      'ICE Standard': 'ICE Standard', // ICE cases do not have other types
    };

    const type = typeToggleMap[dicom?.type] || 'Standard'; // Fallback to 'Standard' if type is undefined

    // Update the dicom type in the state
    setActiveType(type);
    setDicom(dicom => ({
      ...dicom,
      type,
    }));
  };

  const setViewLabel = useCallback(
    payload => {
      let nonActiveViewLabels, activeViewLabel, newViewLabel;

      // in review-mode, override the review users active view-label
      if (isReviewMode) {
        nonActiveViewLabels = viewLabelsResponse?.filter(
          viewLabel =>
            viewLabel?.dicom !== activeDicom?.uuid && viewLabel?.userEmail === user?.profile?.email
        );
        activeViewLabel = viewLabelsResponse?.find(
          viewLabel =>
            viewLabel?.dicom === activeDicom?.uuid && viewLabel?.userEmail === user?.profile?.email
        );
        newViewLabel = {
          ...activeViewLabel,
          ...payload,
          userEmail: payload.userEmail || activeUser.email,
        };
      }

      // in normal mode, override the active users active view-label
      else {
        nonActiveViewLabels = viewLabels?.filter(
          viewLabel => viewLabel?.dicom !== activeDicom?.uuid
        );
        const activeViewLabel = viewLabels?.find(
          viewLabel => viewLabel?.dicom === activeDicom?.uuid
        );
        newViewLabel = {
          ...activeViewLabel,
          ...payload,
          userEmail: payload.userEmail || activeUser.email,
        };
      }
      setViewLabels([...nonActiveViewLabels, newViewLabel]);
      setViewLabelChangesAreSaved(false);
    },
    [activeDicom, viewLabels, isReviewMode]
  );

  const setActiveQualityLabel = useCallback(
    qualityLabel => {
      const nonActiveQualityLabels = qualityLabels?.filter(
        qualityLabel =>
          qualityLabel?.dicom !== activeDicom?.uuid &&
          (isReviewMode ? qualityLabel?.userEmail === user?.profile?.email : true)
      );
      const activeQualityLabel = {
        ...qualityLabel,
        userEmail: qualityLabel.userEmail || activeUser.email,
      };
      setQualityLabels([...nonActiveQualityLabels, activeQualityLabel]);
      setQualityLabelChangesAreSaved(false);
    },
    [activeDicom, qualityLabels, isReviewMode]
  );

  const setPhaseLabel = useCallback(
    newPhaseLabel => {
      const nonActivePhaseLabels = phaseLabels?.filter(
        phaseLabel =>
          phaseLabel?.frame !== activeFrame?.uuid &&
          !(
            newPhaseLabel?.phaseClass === phaseLabel?.phaseClass &&
            newPhaseLabel?.dicom === phaseLabel?.dicom
          ) &&
          (isReviewMode ? phaseLabel?.userEmail === user?.profile?.email : true)
      );
      const activePhaseLabel = {
        ...newPhaseLabel,
        userEmail: newPhaseLabel.userEmail || activeUser.email,
      };
      setPhaseLabels([...nonActivePhaseLabels, activePhaseLabel]);
      setPhaseLabelLabelChangesAreSaved(false);
    },
    [activeFrame?.uuid, phaseLabels, isReviewMode]
  );

  const setKeypointCollectionLabel = useCallback(
    keypointCollectionLabel => {
      const nonActiveKeypointCollections = keypointCollectionLabels?.filter(
        keypointCollectionLabel =>
          (keypointCollectionLabel?.frame !== activeFrame?.uuid ||
            keypointCollectionLabel?.keypointCollectionClass !==
              activeKeypointCollectionClass?.uuid) &&
          (isReviewMode ? keypointCollectionLabel?.userEmail === user?.profile?.email : true)
      );
      const newKeypointCollectionLabel = {
        ...keypointCollectionLabel,
        frame: activeFrame?.uuid,
        frameIndex: activeFrameIndex,
        keypointCollectionClass: activeKeypointCollectionClass?.uuid,
        keypointCollectionClassValue: activeKeypointCollectionClass?.value,
        userEmail: keypointCollectionLabel.userEmail || activeUser.email,
      };
      setKeypointCollectionLabelLabels([
        ...nonActiveKeypointCollections,
        newKeypointCollectionLabel,
      ]);
      setKeypointCollectionLabelLabelsChangesAreSaved(false);
    },
    [
      activeFrame?.uuid,
      activeFrameIndex,
      activeKeypointCollectionClass,
      keypointCollectionLabels,
      isReviewMode,
    ]
  );

  const setSegmentation = useCallback(
    payload => {
      // if the class is passed in via the payload use it, otherwise use the active class as a default
      // eventually change this so that each function call sends the desired class
      const activeSegmentationClassPayload =
        segmentationClasses?.find(sc => sc?.uuid === payload.segmentationClass) ||
        activeSegmentationClass;

      const currentSegmentations = Array.isArray(allSegmentationLabels)
        ? allSegmentationLabels
        : [];
      const targetUserEmail = payload.userEmail || activeUser.email;
      const activeSegmentationIndex = currentSegmentations.findIndex(segmentation => {
        if (payload.uuid) return segmentation?.uuid === payload.uuid;
        return (
          segmentation?.frame === activeFrame?.uuid &&
          segmentation?.segmentationClass === activeSegmentationClassPayload?.uuid &&
          segmentation?.userEmail === targetUserEmail
        );
      });
      const activeSegmentation = currentSegmentations[activeSegmentationIndex];
      const newSegmentation = {
        ...activeSegmentation,
        ...payload,
        segmentationClass: activeSegmentationClassPayload?.uuid,
        segmentationClassValue: activeSegmentationClassPayload?.value,
        dicom: activeDicom?.uuid,
        frame: activeFrame?.uuid,
        frameIndex: activeFrame?.index,
        userEmail: targetUserEmail,
      };
      const nextSegmentations = [...currentSegmentations];
      if (activeSegmentationIndex === -1) nextSegmentations.push(newSegmentation);
      else nextSegmentations[activeSegmentationIndex] = newSegmentation;

      setSegmentationLabels(nextSegmentations);
      markSegmentationAsDirty(newSegmentation);
    },
    [
      activeUser.email,
      allSegmentationLabels,
      markSegmentationAsDirty,
      activeSegmentationClass?.uuid,
      activeSegmentationClass?.value,
      activeDicom?.uuid,
      activeFrame?.index,
      activeFrame?.uuid,
      segmentationClasses,
    ]
  );

  // keypointer tool:
  const activeKeypoints = useMemo(() => {
    const keypointLabels = activeKeypointCollectionClass?.keypointClasses?.map(keypointClass => {
      const keypoint = activeKeypointCollectionLabels?.keypointLabels?.find(
        keypoint => keypoint?.keypointClass === keypointClass?.uuid
      );
      const isLabeled = !!keypoint?.x && !!keypoint?.y;
      const isActive =
        keypoint?.isActive === undefined && !isLabeled && keypointClass?.order === 1
          ? true
          : keypoint?.isActive;

      return {
        ...keypoint,
        order: keypointClass?.order,
        frame: activeFrame?.uuid,
        frameIndex: activeFrameIndex,
        keypointClass: keypointClass?.uuid,
        keypointClassValue: keypointClass?.value,
        keypointPair: keypointClass?.keypointPair,
        isActive,
        isLabeled,
      };
    });
    return keypointLabels;
  }, [
    activeFrame?.uuid,
    activeFrameIndex,
    activeKeypointCollectionLabels?.keypointLabels,
    activeKeypointCollectionClass?.keypointClasses,
  ]);

  const setKeypoints = useCallback(
    keypointLabels => {
      setKeypointCollectionLabel({
        ...activeKeypointCollectionLabels,
        keypointLabels: keypointLabels,
        dicom: activeDicom?.uuid,
      });
    },
    [activeDicom?.uuid, activeKeypointCollectionLabels, setKeypointCollectionLabel]
  );

  const playVideo = useCallback(
    speed => {
      setVideoState(speed);
      const fps = numberOfSeconds * speed;
      const id = setInterval(() => {
        setActiveFrameIndex(activeFrameIndex => {
          if (activeFrameIndex === numberOfFrames - 1) return 0;
          return activeFrameIndex + 1;
        });
      }, fps);
      setIntervalId(id);
    },
    [numberOfFrames, numberOfSeconds]
  );

  const pauseVideo = useCallback(() => {
    if (intervalId !== null) {
      setVideoState(0);
      clearInterval(intervalId);
      setIntervalId(null);
    }
  }, [intervalId]);

  useEffect(() => {
    const videoIsPlaying = Boolean(videoState);
    const videoStopped = wasVideoPlayingRef.current && !videoIsPlaying;
    wasVideoPlayingRef.current = videoIsPlaying;

    if (!videoStopped || !activeFrame) return;
    const route = getFrameRouteWithReviewMode(activeFrame);
    if (route) navigate(route, { replace: true });
  }, [activeFrame, getFrameRouteWithReviewMode, navigate, videoState]);

  const handleFilterApply = useCallback((filters: IFilterValues) => {
    setViewFilters(filters);
  }, []);

  const handleFilterToggle = useCallback(() => {
    setFilterIsOpen(prev => !prev);
  }, []);

  const handleFilterClose = useCallback(() => {
    setFilterIsOpen(false);
  }, []);

  // populate initial values if provided in query params
  useEffect(() => {
    const reviewParam = searchParams.get('isReviewMode');
    const segClass = segmentationClasses?.find(
      sc => sc?.value === searchParams.get('activeSegmentationClass')
    );

    if (reviewParam !== null) setIsReviewMode(reviewParam === 'true');
    if (segClass) {
      setActiveTool('segmentor');
      setActiveSegmentationClass(segClass);
    }

    // activeUser query param is no longer supported — always use the current logged-in user
    setActiveUser({ email: user?.profile?.email });
  }, [searchParams, user, segmentationClasses]);

  // Load a specific frame from either the legacy frame ICID route or the frame index route.
  useEffect(() => {
    if (!frames?.length) return;

    let idx = -1;
    if (frameIndex !== undefined && /^\d+$/.test(frameIndex)) {
      const requestedFrameIndex = Number(frameIndex);
      if (Number.isSafeInteger(requestedFrameIndex) && requestedFrameIndex >= 0) {
        // Use the API-provided frame index instead of assuming it matches the array position.
        idx = frames.findIndex(frame => frame.index === requestedFrameIndex);
      }
    } else if (frameIcid) {
      idx = frames.findIndex(frame => frame.icid === frameIcid);
    }

    if (idx !== -1) {
      setActiveFrameIndex(idx);
    }
  }, [frames, frameIcid, frameIndex]);

  // reset the state when a new dicom is loaded

  useEffect(() => {
    if (!dicomIcid) return;
    if (!isReviewMode && !study?.isIceStudy) setActiveTool('identifier');
    pauseVideo();
    setEditMode(false);
    setActiveFrameIndex(0);
    setViewLabelChangesAreSaved(true);
    setQualityLabelChangesAreSaved(true);
    setPhaseLabelLabelChangesAreSaved(true);
    setKeypointCollectionLabelLabelsChangesAreSaved(true);
    setSegmentationChangesAreSaved(true);
    setDicomJobChangesAreSaved(true);
    clearDirtySegmentations();
    // setShowSegmentationPredictions(false);
    // setShowSegmentationReferences(false);
    // setActiveTab('labels');
    // setSegmentationObjectToBeAdopted(null);
  }, [clearDirtySegmentations, dicomIcid]);

  useEffect(() => {
    setSelectedSegmentationPredictionSource(currentSource =>
      currentSource !== undefined && segmentationPredictionSourceNames.includes(currentSource)
        ? currentSource
        : (segmentationPredictionSourceNames[0] ?? null)
    );
  }, [segmentationPredictionSourceNames]);

  useEffect(() => {
    if (!segmentationPredictions) return;

    setActiveSegmentationPredictionClasses(
      segmentationPredictions
        .filter(p => p.sourceName === activeSegmentationPredictionSource)
        .map(p => p.segmentationClassValue)
    );
  }, [segmentationPredictions, activeSegmentationPredictionSource]);

  useEffect(() => {
    if (!dicoms || !dicomIcid) return;
    const dicom = dicoms?.find(dicom => dicom?.uuid === activeDicom?.uuid);
    let activeType = dicom?.type;

    // special case for dicoms with type = "Single Frame" which is not supported
    if (activeDicom?.type === 'Single Frame') activeType = 'Standard';

    setDicom(dicom);
    setActiveType(activeType as string);
  }, [dicoms, dicomIcid]);

  useEffect(() => {
    if (!framesResponse || !activeDicom) return;
    setFrames(framesResponse.filter(frame => frame.dicom === activeDicom?.uuid));
  }, [framesResponse, activeDicom]);

  // when review mode is turned off, reset active user
  useEffect(() => {
    if (!isReviewMode) setActiveUser({ email: user?.profile?.email });
  }, [isReviewMode, user]);

  useEffect(() => {
    const enteringReviewMode = isReviewMode && !wasReviewModeRef.current;

    if (enteringReviewMode) {
      setShowOtherSegmentations(true);
    }

    wasReviewModeRef.current = isReviewMode;
  }, [isReviewMode]);

  useEffect(() => setViewLabels(viewLabelsResponse), [viewLabelsResponse]);
  useEffect(() => setQualityLabels(qualityLabelsResponse), [qualityLabelsResponse]);
  useEffect(() => setPhaseLabels(phaseLabelsResponse), [phaseLabelsResponse]);
  useEffect(
    () => setKeypointCollectionLabelLabels(keypointCollectionLabelsResponse),
    [keypointCollectionLabelsResponse]
  );
  useEffect(() => {
    // 1. Clear old dirty states first, so we don't need a setTimeout hack later
    clearDirtySegmentations();

    const initialLabels = segmentationLabelsResponse ? [...segmentationLabelsResponse] : [];

    // We no longer clone the label here. Instead, we let the user view/edit the original,
    // and strip the uuid/icid right before saving (in Actions.tsx) to force a backend CREATE.

    setSegmentationLabels(initialLabels);
  }, [
    clearDirtySegmentations,
    segmentationLabelsResponse,
    searchParams,
    user?.profile?.email,
    markSegmentationAsDirty,
  ]);

  useEffect(
    () => setSegmentationPredictions(segmentationPredictionsResponse),
    [segmentationPredictionsResponse]
  );

  useEffect(
    () => setSegmentationReferences(segmentationReferencesResponse),
    [segmentationReferencesResponse]
  );

  useEffect(
    () => setActiveKeypointCollectionClass(dicomKeypointCollectionClasses?.[0]),
    [dicomKeypointCollectionClasses]
  );
  useEffect(() => {
    if (!dicomFeatures?.length) return;

    setActiveSegmentationClass(
      activeClass =>
        dicomFeatures.find(feature => feature.uuid === activeClass?.uuid) ?? dicomFeatures[0]
    );
  }, [dicomFeatures]);

  // wait 1 second for all components to mount
  useEffect(() => {
    const apisAreLoading =
      studyResponseIsLoading ||
      annotationSetIsLoading ||
      studyLabelingJobResponseIsLoading ||
      dicomsResponseIsLoading ||
      dicomLabelingJobsResponseIsLoading ||
      framesResponseIsLoading ||
      viewLabelResponseIsLoading ||
      qualityLabelsResponseIsLoading ||
      phaseLabelsResponseIsLoading ||
      keypointCollectionLabelsResponseIsLoading ||
      segmentationLabelsResponseIsLoading ||
      segmentationPredictionsResponseIsLoading ||
      segmentationReferencesResponseIsLoading ||
      usersResponseIsLoading;

    if (apisAreLoading) {
      setDataIsLoading(true);
      const apisLoaded = new Promise(resolve => {
        const checkApis = () => (apisAreLoading ? setTimeout(checkApis, 1000) : resolve(void 0));
        checkApis();
      });

      const oneSecondWait = new Promise(resolve => setTimeout(resolve, 1000));

      Promise.all([apisLoaded, oneSecondWait]).then(() => setDataIsLoading(false));
    } else setTimeout(() => setDataIsLoading(false), 1000);
  }, [
    annotationSetIsLoading,
    viewLabelResponseIsLoading,
    qualityLabelsResponseIsLoading,
    phaseLabelsResponseIsLoading,
    keypointCollectionLabelsResponseIsLoading,
    segmentationLabelsResponseIsLoading,
    dicomsResponseIsLoading,
    dicomLabelingJobsResponseIsLoading,
    framesResponseIsLoading,
    studyLabelingJobResponseIsLoading,
    segmentationPredictionsResponseIsLoading,
    segmentationReferencesResponseIsLoading,
  ]);

  useEffect(() => {
    if (!study || !dicoms) setDataIsLoading(true);
  }, [study, dicoms]);

  useHotkeys('R', event => {
    event.preventDefault();
    if (previousButtonIsDisabled) return;
    setActiveTool(activeTool =>
      activeTool === 'phaser'
        ? 'identifier'
        : activeTool === 'keypointer'
          ? showPhaser
            ? 'phaser'
            : 'identifier'
          : activeTool === 'segmentor'
            ? showKeypointer
              ? 'keypointer'
              : showPhaser
                ? 'phaser'
                : 'identifier'
            : 'identifier'
    );
  });

  useEffect(() => {
    if (isReviewMode && activeSegmentation) setSegmentationObjectToBeAdopted(activeSegmentation);
    // else setSegmentationObjectToBeAdopted(null);
  }, [isReviewMode, activeSegmentation]);

  useHotkeys('Y', event => {
    event.preventDefault();
    if (nextButtonIsDisabled) return;
    setActiveTool(activeTool =>
      activeTool === 'identifier'
        ? showPhaser
          ? 'phaser'
          : showKeypointer
            ? 'keypointer'
            : showSegmentor
              ? 'segmentor'
              : 'identifier'
        : activeTool === 'phaser'
          ? showKeypointer
            ? 'keypointer'
            : showSegmentor
              ? 'segmentor'
              : 'identifier'
          : activeTool === 'keypointer'
            ? showSegmentor
              ? 'segmentor'
              : 'identifier'
            : 'identifier'
    );
  });

  const showPageLoader =
    study == null ||
    dicoms == null ||
    frames == null ||
    viewClasses == null ||
    phaseClasses == null ||
    keypointCollectionClasses == null ||
    segmentationClasses == null ||
    viewLabels == null ||
    qualityLabels == null ||
    phaseLabels == null ||
    keypointCollectionLabels == null ||
    segmentationLabels == null ||
    segmentationPredictions == null ||
    segmentationReferences == null; // when entire page is loading
  const showCanvasLoader = showPageLoader || framesResponseIsLoading; // when frames are loading

  // View predictions (filter options + per-dicom view tags) are non-critical: only request them once the page
  // and the active dicom have loaded (or the filter is opened), so they never compete with labeling data.
  const [viewPredictionsEnabled, setViewPredictionsEnabled] = useState(false);
  useEffect(() => {
    if (!viewPredictionsEnabled && (!showCanvasLoader || filterIsOpen)) {
      setViewPredictionsEnabled(true);
    }
  }, [viewPredictionsEnabled, showCanvasLoader, filterIsOpen]);
  const { data: viewPredictionsSummary, isFetching: viewPredictionsSummaryIsLoading } =
    useGetViewPredictionsSummaryQuery(studyLabelingJob?.uuid as string, {
      skip: !viewPredictionsEnabled || !studyLabelingJob?.uuid,
    });
  const showSavingLoader = dataIsSaving;
  const showErrorIcon = !dataIsSaving && errorOccured;
  const showUnsavedChanges = !dataIsSaving && !showErrorIcon && !changesAreSaved;

  return (
    <ViewerContainer>
      <HeaderContainer>
        {!showPageLoader && (
          <>
            <LeftSideHeaderContainer>
              {annotationSet && (
                <Button>
                  <a href={backToDatasetLink}>
                    <IoMdArrowRoundBack />
                    <span>Back to Dataset</span>
                  </a>
                </Button>
              )}
              <FilterIconButton
                $isActive={
                  filterIsOpen ||
                  viewFilters.pipelines.length > 0 ||
                  viewFilters.viewClasses.length > 0
                }
                onClick={handleFilterToggle}
              >
                <IoMdFunnel />
              </FilterIconButton>
              {annotationSet && (
                <StudyIdContainer>Annotation Set: {annotationSet?.name}</StudyIdContainer>
              )}
              <StudyIdContainer>Study: {study?.icid}</StudyIdContainer>
              {study?.procedureType && (
                <StudyIdContainer>Procedure type: {study.procedureType}</StudyIdContainer>
              )}
            </LeftSideHeaderContainer>
            <FilterSelector
              isOpen={filterIsOpen}
              onClose={handleFilterClose}
              viewClasses={viewClasses}
              onApply={handleFilterApply}
              initialFilters={viewFilters}
              viewPredictionsSummary={viewPredictionsSummary}
              viewPredictionsLoading={
                viewPredictionsSummaryIsLoading ||
                (!viewPredictionsSummary && !!studyLabelingJob?.uuid)
              }
            />
            {isReviewMode && activeReviewFrame && (
              <ReviewFrameHeader>
                {activeReviewFrame.decision ?? ''} - {activeReviewFrame.comment ?? ''}
              </ReviewFrameHeader>
            )}
            <LabelsContainer>
              {!showCanvasLoader && (
                <>
                  <StatusTag isHidden={dataIsLoading} isLabeled={dicomIsLabeled}>
                    {dicomIsLabeled ? 'Labeled' : ''}
                  </StatusTag>
                  {showSegmentationTag && <LabelTag type="segmentor">{segmentationTag}</LabelTag>}
                  {showKeypointTag && <LabelTag type="keypointer">{keypointTag}</LabelTag>}
                  {showPhaseTag && <LabelTag type="phaser">{phaseTag}</LabelTag>}
                  {showWindowAndViewTag && (
                    <LabelTag type="identifier">{windowAndViewTag}</LabelTag>
                  )}
                  <LabelTag onClick={handleDicomTypeOnClick} type="type">
                    {activeType}
                  </LabelTag>
                </>
              )}
              <SmallLoaderContainer>
                <IconContainer isHidden={!showUnsavedChanges} size={30}>
                  <IoIosCloudUpload color="#9b257b" />
                </IconContainer>
                <IconContainer isHidden={!showErrorIcon} size={30}>
                  <IoIosWarning color="red" />
                </IconContainer>
                <IconContainer isHidden={!showSavingLoader}>
                  <Loading size={25} color="#ccc" ariaLabel="Saving changes" />
                </IconContainer>
              </SmallLoaderContainer>
            </LabelsContainer>
          </>
        )}
      </HeaderContainer>
      <PageContainer>
        <SubHeader
          activeDicomLabelingJob={activeDicomLabelingJob}
          activePhaseLabels={activePhaseLabels}
          activeQualityLabel={activeQualityLabel}
          activeReviewFrame={activeReviewFrame}
          activeViewLabel={activeViewLabel}
          checkIfDicomIsLabeled={checkIfDicomIsLabeled}
          dicom={dicom}
          dicomLabelingJob={dicomIcid}
          dicoms={dicoms}
          editMode={editMode}
          frames={frames}
          identifierIsActive={identifierIsActive}
          identifierIsDisabled={identifierIsDisabled}
          keypointerIsActive={keypointerIsActive}
          keypointerIsDisabled={keypointerIsDisabled}
          keypointCollectionLabels={keypointCollectionLabels}
          nextButtonIsDisabled={nextButtonIsDisabled}
          nextReviewIsDisabled={!reviewFrameNavigation.next}
          onNextReview={goToNextReview}
          onPreviousReview={goToPreviousReview}
          pageIsLoading={showPageLoader}
          phaserIsActive={phaserIsActive}
          phaserIsDisabled={phaserIsDisabled}
          previousButtonIsDisabled={previousButtonIsDisabled}
          previousReviewIsDisabled={!reviewFrameNavigation.previous}
          dirtySegmentations={dirtySegmentations}
          segmentorIsActive={segmentorIsActive}
          segmentorIsDisabled={segmentorIsDisabled}
          clearDirtySegmentations={clearDirtySegmentations}
          setDataIsSaving={setDataIsSaving}
          setDicomJobChangesAreSaved={setDicomJobChangesAreSaved}
          setErrorOccured={setErrorOccured}
          setKeypointCollectionLabelLabelsChangesAreSaved={
            setKeypointCollectionLabelLabelsChangesAreSaved
          }
          setPhaseLabelLabelChangesAreSaved={setPhaseLabelLabelChangesAreSaved}
          setQualityLabelChangesAreSaved={setQualityLabelChangesAreSaved}
          setSegmentationChangesAreSaved={setSegmentationChangesAreSaved}
          setViewLabelChangesAreSaved={setViewLabelChangesAreSaved}
          setActiveTool={setActiveTool}
          showKeypointer={showKeypointer}
          showPhaser={showPhaser}
          showSegmentor={showSegmentor}
          study={study as IStudy}
          studyLabelingJob={studyLabelingJob}
          isReviewMode={isReviewMode}
        />
        <ContentContainer>
          <RightSideBar isHidden={showCanvasLoader} isOpen={dicomSideBarIsOpen}>
            <SideBarContainer>
              {!showPageLoader && (
                <Dicoms
                  dicoms={sidebarDicoms}
                  checkIfDicomIsLabeled={checkIfDicomIsLabeled}
                  changesAreSaved={changesAreSaved}
                  dicomSideBarIsOpen={dicomSideBarIsOpen}
                  setDicomSideBarIsOpen={setDicomSideBarIsOpen}
                  dicomViewPredictions={viewPredictionsSummary?.dicomPredictions}
                  selectedPipelines={viewFilters.pipelines}
                  rejectedDicomUuids={rejectedDicomUuids}
                />
              )}
            </SideBarContainer>
          </RightSideBar>
          <CanvasContainer isHidden={!showCanvasLoader}>
            <Loader />
          </CanvasContainer>
          <CanvasContainer isHidden={showCanvasLoader}>
            <Canvas
              study={study}
              dicom={dicom}
              dicoms={dicoms}
              changesAreSaved={changesAreSaved}
              frames={frames}
              playVideo={playVideo}
              pauseVideo={pauseVideo}
              videoState={videoState}
              activeFrame={activeFrame}
              activeKeypoints={activeKeypoints}
              phaseLabels={activePhaseLabels}
              setFrames={setFrames}
              setActiveFrame={setActiveFrameWithUrl}
              setKeypoints={setKeypoints}
              showMeasurements={showMeasurements}
              keypointerIsActive={keypointerIsActive}
              segmentorIsActive={segmentorIsActive}
              activeSegmentation={activeSegmentation}
              allSegmentations={allSegmentations}
              allSegmentationPredictions={allSegmentationPredictions}
              allSegmentationReferences={allSegmentationReferences}
              setActiveSegmentation={setSegmentation}
              useOpenContours={useOpenContours}
              showOtherSegmentations={showOtherSegmentations}
              showSegmentationPredictions={showSegmentationPredictions}
              showSegmentationReferences={showSegmentationReferences}
              activeSegmentationClass={activeSegmentationClass}
              setSegmentationObjectToBeAdopted={setSegmentationObjectToBeAdopted}
              editMode={editMode}
              unfilteredSegmentationReferences={segmentationReferences}
              activeSegmentationPredictionClasses={activeSegmentationPredictionClasses}
              activeSegmentationReferenceClasses={activeSegmentationReferenceClasses}
              activeOtherSegmentationClasses={activeOtherSegmentationClasses}
              setActiveOtherSegmentationClasses={setActiveOtherSegmentationClasses}
              pnoExistsResponse={pnoExistsResponse}
              rejectedFrameIndexes={rejectedFrameIndexes}
            />
          </CanvasContainer>
          <RightSideBar isHidden={showCanvasLoader} isOpen>
            {showPageLoader ? (
              <SideBarContainer />
            ) : (
              <>
                {segmentorIsActive && (
                  <TabContainer>
                    <Tab isActive={activeTab === 'labels'} onClick={() => setActiveTab('labels')}>
                      Labels
                    </Tab>
                    <Tab
                      isActive={activeTab === 'predictions'}
                      onClick={() => setActiveTab('predictions')}
                    >
                      Predictions
                    </Tab>
                    <Tab
                      isActive={activeTab === 'references'}
                      onClick={() => setActiveTab('references')}
                    >
                      References
                    </Tab>
                  </TabContainer>
                )}

                {!segmentorIsActive || activeTab === 'labels' ? (
                  <>
                    <SideBarContainer>
                      {identifierIsActive && (
                        <>
                          <Qualifier
                            dicom={dicom}
                            qualityLabel={activeQualityLabel}
                            setQualityLabel={setActiveQualityLabel}
                          />
                          <Identifier
                            dicom={dicom}
                            viewClasses={dicomViewClasses}
                            viewLabel={activeViewLabel}
                            setViewLabel={setViewLabel}
                          />
                        </>
                      )}
                      {showPhaser && phaserIsActive && (
                        <Phaser
                          activeFrame={activeFrame}
                          dicom={dicom}
                          phaseClasses={phaseClasses}
                          phaseLabels={activePhaseLabels}
                          setPhaseLabel={setPhaseLabel}
                        />
                      )}
                      {showKeypointer && keypointerIsActive && (
                        <Keypointer
                          dicom={dicom}
                          activeFrameIndex={activeFrameIndex}
                          activeFrame={activeFrame}
                          phaseLabels={activePhaseLabels}
                          activeKeypoints={activeKeypoints}
                          keypointCollectionClasses={dicomKeypointCollectionClasses}
                          activeKeypointCollectionClass={activeKeypointCollectionClass}
                          keypointCollectionLabels={keypointCollectionLabels}
                          showMeasurements={showMeasurements}
                          showPhaser={showPhaser}
                          setActiveFrameIndex={setActiveFrameIndex}
                          setActiveKeypointCollectionClass={setActiveKeypointCollectionClass}
                          setKeypoints={setKeypoints}
                          setShowMeasurements={setShowMeasurements}
                        />
                      )}
                      {showSegmentor && segmentorIsActive && (
                        <Segmentor
                          activeFrame={activeFrame}
                          activeSegmentationClass={activeSegmentationClass}
                          activeSegmentation={activeSegmentation}
                          segmentationClasses={dicomFeatures}
                          dicom={dicom}
                          phaseLabels={activePhaseLabels}
                          segmentationReferences={segmentationReferences}
                          segmentations={displayedSegmentationLabels}
                          setActiveSegmentationClass={handleChangeSegmentationClass}
                          setActiveFrameIndex={setActiveFrameIndex}
                          setActiveSegmentation={setSegmentation}
                          setSegmentation={setSegmentation}
                          useOpenContours={useOpenContours}
                          setUseOpenContours={setUseOpenContours}
                          showOtherSegmentations={showOtherSegmentations}
                          setShowOtherSegmentations={setShowOtherSegmentations}
                          showSegmentationReferences={showSegmentationReferences}
                          adoptSegmentation={handleAdoptSegmentation}
                          study={study}
                          editMode={editMode}
                          showSegmentationPredictions={showSegmentationPredictions}
                          activeOtherSegmentationClasses={activeOtherSegmentationClasses}
                          setActiveOtherSegmentationClasses={setActiveOtherSegmentationClasses}
                        />
                      )}
                    </SideBarContainer>
                  </>
                ) : activeTab === 'predictions' ? (
                  <SideBarContainer>
                    <Predictions
                      segmentationPredictions={segmentationPredictions}
                      selectedSource={activeSegmentationPredictionSource}
                      setSelectedSource={setSelectedSegmentationPredictionSource}
                      editMode={editMode}
                      setShowSegmentationPredictions={setShowSegmentationPredictions}
                      showSegmentationPredictions={showSegmentationPredictions}
                      activeFrameUuid={activeFrame?.uuid}
                      setActiveTool={setActiveTool}
                      activeSegmentationPredictionClasses={activeSegmentationPredictionClasses}
                      setActiveSegmentationPredictionClasses={
                        setActiveSegmentationPredictionClasses
                      }
                      segmentationLabels={segmentationLabels}
                      setSegmentationLabels={setSegmentationLabels}
                      markSegmentationAsDirty={markSegmentationAsDirty}
                      setShowOtherSegmentations={setShowOtherSegmentations}
                    />
                  </SideBarContainer>
                ) : (
                  <SideBarContainer>
                    <References
                      activeFrameIndex={activeFrameIndex}
                      activeSegmentationClass={activeSegmentationClass}
                      activeSegmentationReferenceClasses={activeSegmentationReferenceClasses}
                      editMode={editMode}
                      segmentationReferences={dicomSegmentationReferences}
                      showSegmentationReferences={showSegmentationReferences}
                      setActiveSegmentationClass={setActiveSegmentationClass}
                      setActiveSegmentationReferenceClasses={setActiveSegmentationReferenceClasses}
                      setShowSegmentationReferences={setShowSegmentationReferences}
                      segmentationClasses={dicomFeatures}
                      setActiveTool={setActiveTool}
                      adoptSegmentation={handleAdoptSegmentation}
                      setActiveFrameIndex={setActiveFrameIndex}
                    />
                  </SideBarContainer>
                )}
              </>
            )}
          </RightSideBar>
        </ContentContainer>
      </PageContainer>
    </ViewerContainer>
  );
};

export default Viewer;
